// DomainDetectorAdminView.js

import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';

const DomainDetectorAdminView = () => {
    const { getToken } = useAuth();
    const [pendingDetections, setPendingDetections] = useState([]);
    const [selectedDetection, setSelectedDetection] = useState(null);
    const [filterStatus, setFilterStatus] = useState('pending');
    const [filterCompany, setFilterCompany] = useState('all');

    useEffect(() => {
        loadPendingDetections();
    }, [filterStatus, filterCompany]);

    const loadPendingDetections = async () => {
        const token = await getToken();
        const response = await fetch(
            `/api/domain-detector/admin/pending?status=${filterStatus}&companyId=${filterCompany}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = await response.json();
        setPendingDetections(data.detections);
    };

    const approveDetection = async (detectionId, notes) => {
        const token = await getToken();
        await fetch(`/api/domain-detector/admin/approve/${detectionId}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ adminNotes: notes })
        });
        loadPendingDetections();
    };

    const rejectDetection = async (detectionId, notes) => {
        const token = await getToken();
        await fetch(`/api/domain-detector/admin/reject/${detectionId}`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ adminNotes: notes })
        });
        loadPendingDetections();
    };

    return (
        <div style={{ display: 'flex', height: '100vh' }}>
            {/* LEFT SIDEBAR - Detections List */}
            <div style={{ width: '400px', borderRight: '1px solid #ddd', overflowY: 'auto' }}>
                {/* Filters */}
                <div style={{ padding: '15px', borderBottom: '1px solid #ddd' }}>
                    <h3>🚨 Domain Impersonation Feed</h3>
                    
                    <select 
                        value={filterStatus} 
                        onChange={(e) => setFilterStatus(e.target.value)}
                        style={{ width: '100%', marginBottom: '10px', padding: '8px' }}
                    >
                        <option value="pending">Pending Review</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="investigating">Investigating</option>
                    </select>

                    <select 
                        value={filterCompany} 
                        onChange={(e) => setFilterCompany(e.target.value)}
                        style={{ width: '100%', padding: '8px' }}
                    >
                        <option value="all">All Companies</option>
                        {/* Map company list here */}
                    </select>
                </div>

                {/* Detection Cards */}
                {pendingDetections.map(detection => (
                    <div 
                        key={detection.id}
                        onClick={() => setSelectedDetection(detection)}
                        style={{
                            padding: '15px',
                            borderBottom: '1px solid #eee',
                            cursor: 'pointer',
                            backgroundColor: selectedDetection?.id === detection.id ? '#f0f8ff' : 'white'
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <strong>{detection.domain}</strong>
                            <span style={{
                                backgroundColor: detection.risk_score > 70 ? '#ff4444' : 
                                                detection.risk_score > 40 ? '#ffaa00' : '#44ff44',
                                color: 'white',
                                padding: '2px 8px',
                                borderRadius: '10px',
                                fontSize: '12px'
                            }}>
                                {detection.risk_score}
                            </span>
                        </div>
                        
                        <div style={{ fontSize: '13px', color: '#666' }}>
                            <div>Company: {detection.company_name}</div>
                            <div>Detected: {new Date(detection.detected_date).toLocaleDateString()}</div>
                            <div>
                                {detection.dns_active && '🌐 DNS Active '}
                                {detection.has_ssl && '🔒 SSL '}
                            </div>
                            {detection.matched_danger_keywords?.length > 0 && (
                                <div style={{ color: '#ff4444' }}>
                                    ⚠️ {detection.matched_danger_keywords.join(', ')}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* RIGHT PANEL - Detection Details */}
            {selectedDetection && (
                <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
                    <h2>{selectedDetection.domain}</h2>
                    
                    {/* Risk Score */}
                    <div style={{ marginBottom: '20px' }}>
                        <h3>Risk Score: {selectedDetection.risk_score}/100</h3>
                        <div style={{ 
                            width: '100%', 
                            height: '20px', 
                            backgroundColor: '#eee',
                            borderRadius: '10px',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                width: `${selectedDetection.risk_score}%`,
                                height: '100%',
                                backgroundColor: selectedDetection.risk_score > 70 ? '#ff4444' : 
                                              selectedDetection.risk_score > 40 ? '#ffaa00' : '#44ff44'
                            }} />
                        </div>
                    </div>

                    {/* Technical Details */}
                    <div style={{ marginBottom: '20px' }}>
                        <h3>Technical Details</h3>
                        <table style={{ width: '100%' }}>
                            <tbody>
                                <tr><td>DNS Active:</td><td>{selectedDetection.dns_active ? '✅ Yes' : '❌ No'}</td></tr>
                                {selectedDetection.ip_address && (
                                    <tr><td>IP Address:</td><td>{selectedDetection.ip_address}</td></tr>
                                )}
                                <tr><td>SSL Certificate:</td><td>{selectedDetection.has_ssl ? '✅ Yes' : '❌ No'}</td></tr>
                                {selectedDetection.ssl_issuer && (
                                    <tr><td>SSL Issuer:</td><td>{selectedDetection.ssl_issuer}</td></tr>
                                )}
                                <tr><td>Registrar:</td><td>{selectedDetection.registrar || 'Unknown'}</td></tr>
                                <tr><td>Detected:</td><td>{new Date(selectedDetection.detected_date).toLocaleString()}</td></tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Matched Keywords */}
                    <div style={{ marginBottom: '20px' }}>
                        <h3>Matched Keywords</h3>
                        <div>
                            <strong>Brand:</strong> {selectedDetection.matched_brand_keywords?.join(', ') || 'None'}
                        </div>
                        <div style={{ color: '#ff4444' }}>
                            <strong>Danger:</strong> {selectedDetection.matched_danger_keywords?.join(', ') || 'None'}
                        </div>
                    </div>

                    {/* Screenshot */}
                    {selectedDetection.screenshot_url && (
                        <div style={{ marginBottom: '20px' }}>
                            <h3>Screenshot</h3>
                            <img src={selectedDetection.screenshot_url} style={{ maxWidth: '100%' }} alt="Site screenshot" />
                        </div>
                    )}

                    {/* Action Buttons */}
                    {selectedDetection.status === 'pending' && (
                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                            <button 
                                onClick={() => {
                                    const notes = prompt('Admin notes (optional):');
                                    approveDetection(selectedDetection.id, notes);
                                }}
                                style={{
                                    flex: 1,
                                    padding: '15px',
                                    backgroundColor: '#44ff44',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    fontWeight: 'bold'
                                }}
                            >
                                ✅ Approve & Push to Tenant
                            </button>
                            
                            <button 
                                onClick={() => {
                                    const notes = prompt('Rejection reason:');
                                    if (notes) rejectDetection(selectedDetection.id, notes);
                                }}
                                style={{
                                    flex: 1,
                                    padding: '15px',
                                    backgroundColor: '#ff4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    fontWeight: 'bold'
                                }}
                            >
                                ❌ Reject (False Positive)
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DomainDetectorAdminView;